import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/Input';
import { Building2, Users, FolderOpen, MoreHorizontal, Plus, X, ExternalLink, Link as LinkIcon, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const builders = [{
  id: 1,
  name: 'Excel Homes',
  slug: 'excel',
  status: 'Active',
  projects: 12,
  users: 8,
  buyers: 342,
  joinedDate: '2023-06-15'
}, {
  id: 2,
  name: 'Schell Brothers',
  slug: 'schell',
  status: 'Active',
  projects: 8,
  users: 12,
  buyers: 567,
  joinedDate: '2023-03-22'
}, {
  id: 3,
  name: 'Mattamy Homes',
  slug: 'mattamy',
  status: 'Active',
  projects: 15,
  users: 20,
  buyers: 892,
  joinedDate: '2022-11-10'
}, {
  id: 4,
  name: 'Landmark Homes',
  slug: 'landmark',
  status: 'Trial',
  projects: 2,
  users: 3,
  buyers: 45,
  joinedDate: '2024-01-10'
}, {
  id: 5,
  name: 'Starward Homes',
  slug: 'starward',
  status: 'Active',
  projects: 5,
  users: 6,
  buyers: 234,
  joinedDate: '2023-09-08'
}, {
  id: 6,
  name: 'Brookfield',
  slug: 'brookfield',
  status: 'Active',
  projects: 18,
  users: 25,
  buyers: 1245,
  joinedDate: '2022-08-20'
}];
export function BuildersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddBuilderModal, setShowAddBuilderModal] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [newBuilder, setNewBuilder] = useState({
    name: '',
    email: '',
    contactName: '',
    slug: ''
  });
  const filteredBuilders = builders.filter(builder => builder.name.toLowerCase().includes(searchQuery.toLowerCase()) || builder.slug.toLowerCase().includes(searchQuery.toLowerCase()));
  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 20);
  };
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddBuilderModal) {
        setShowAddBuilderModal(false);
        setNewBuilder({
          name: '',
          email: '',
          contactName: '',
          slug: ''
        });
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showAddBuilderModal]);
  const handleAddBuilder = () => {
    console.log('Adding new builder:', newBuilder);
    setShowAddBuilderModal(false);
    setNewBuilder({
      name: '',
      email: '',
      contactName: '',
      slug: ''
    });
  };
  const handleCopyLink = (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/${slug}/dashboard`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };
  const handleOpenPortal = (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/${slug}/dashboard`, '_blank');
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-633">
        <div className="flex items-center justify-between" data-id="element-634">
          <div data-id="element-635">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-636">Builders</h1>
            <p className="text-slate-500" data-id="element-637">
              Manage builder companies and their portal access
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-639" />} onClick={() => setShowAddBuilderModal(true)} data-id="element-638">
            Add New Builder
          </Button>
        </div>

        <Card noPadding data-id="element-640">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-xl" data-id="element-641">
            <SearchInput className="max-w-sm bg-white" placeholder="Search builders or URL slugs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-642" />
            <div className="flex gap-2" data-id="element-643">
              <Button variant="outline" size="sm" data-id="element-644">
                Filter
              </Button>
              <Button variant="outline" size="sm" data-id="element-645">
                Export
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-646">
            <table className="w-full text-left text-sm" data-id="element-647">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-648">
                <tr data-id="element-649">
                  <th className="px-6 py-4" data-id="element-650">Company Name</th>
                  <th className="px-6 py-4" data-id="element-651">Portal URL</th>
                  <th className="px-6 py-4" data-id="element-652">Status</th>
                  <th className="px-6 py-4" data-id="element-653">Projects</th>
                  <th className="px-6 py-4" data-id="element-654">Buyers</th>
                  <th className="px-6 py-4 text-right" data-id="element-655">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-656">
                {filteredBuilders.length === 0 ? <tr data-id="element-657">
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500" data-id="element-658">
                      No builders found matching "{searchQuery}"
                    </td>
                  </tr> : filteredBuilders.map(builder => <tr key={builder.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/builders/${builder.id}`)} data-id="element-659">
                      <td className="px-6 py-4" data-id="element-660">
                        <div className="flex items-center gap-3" data-id="element-661">
                          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-662">
                            <Building2 className="h-5 w-5 text-slate-600" data-id="element-663" />
                          </div>
                          <span className="font-medium text-slate-900" data-id="element-664">
                            {builder.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-665">
                        <div className="flex items-center gap-2" data-id="element-666">
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-700 font-mono" data-id="element-667">
                            /{builder.slug}
                          </code>
                          <button onClick={e => handleCopyLink(builder.slug, e)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" title="Copy portal URL" data-id="element-668">
                            {copiedSlug === builder.slug ? <Check className="h-4 w-4 text-green-500" data-id="element-669" /> : <Copy className="h-4 w-4" data-id="element-670" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-671">
                        <Badge variant={builder.status === 'Active' ? 'success' : builder.status === 'Trial' ? 'warning' : 'neutral'} data-id="element-672">
                          {builder.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-673">
                        <div className="flex items-center gap-1" data-id="element-674">
                          <FolderOpen className="h-4 w-4 text-slate-400" data-id="element-675" />
                          {builder.projects}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-676">
                        {builder.buyers}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-677">
                        <div className="flex items-center justify-end gap-2" data-id="element-678">
                          <Button variant="outline" size="sm" onClick={e => handleOpenPortal(builder.slug, e)} leftIcon={<ExternalLink className="h-3 w-3" data-id="element-680" />} data-id="element-679">
                            Open Portal
                          </Button>
                          <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      navigate(`/admin/builders/${builder.id}`);
                    }} data-id="element-681">
                            Details
                          </Button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add New Builder Modal */}
      {showAddBuilderModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-682">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-683">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-684">
              <div data-id="element-685">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-686">
                  Add New Builder
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-687">
                  Create a new builder company account
                </p>
              </div>
              <button onClick={() => setShowAddBuilderModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-688">
                <X className="h-5 w-5" data-id="element-689" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-690">
              <div data-id="element-691">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-692">
                  Company Name
                </label>
                <input type="text" value={newBuilder.name} onChange={e => {
              const name = e.target.value;
              setNewBuilder({
                ...newBuilder,
                name,
                slug: newBuilder.slug || generateSlug(name)
              });
            }} placeholder="e.g., Acme Homes" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-693" />
              </div>
              <div data-id="element-694">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-695">
                  Portal URL Slug
                </label>
                <div className="flex items-center gap-2" data-id="element-696">
                  <span className="text-slate-500" data-id="element-697">/</span>
                  <input type="text" value={newBuilder.slug} onChange={e => setNewBuilder({
                ...newBuilder,
                slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
              })} placeholder="acme-homes" className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono" data-id="element-698" />
                </div>
                <p className="text-xs text-slate-500 mt-1" data-id="element-699">
                  This will be the URL for their portal: /
                  {newBuilder.slug || 'slug'}/dashboard
                </p>
              </div>
              <div data-id="element-700">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-701">
                  Primary Contact Name
                </label>
                <input type="text" value={newBuilder.contactName} onChange={e => setNewBuilder({
              ...newBuilder,
              contactName: e.target.value
            })} placeholder="e.g., John Smith" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-702" />
              </div>
              <div data-id="element-703">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-704">
                  Contact Email
                </label>
                <input type="email" value={newBuilder.email} onChange={e => setNewBuilder({
              ...newBuilder,
              email: e.target.value
            })} placeholder="e.g., john@acmehomes.com" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-705" />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-706">
                <p className="text-xs text-blue-800" data-id="element-707">
                  An invitation email will be sent to the contact email with
                  setup instructions.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-708">
              <Button variant="outline" onClick={() => setShowAddBuilderModal(false)} data-id="element-709">
                Cancel
              </Button>
              <Button onClick={handleAddBuilder} disabled={!newBuilder.name || !newBuilder.email || !newBuilder.contactName || !newBuilder.slug} leftIcon={<Plus className="h-4 w-4" data-id="element-711" />} data-id="element-710">
                Add Builder
              </Button>
            </div>
          </div>
        </div>}
    </>;
}